import { USE_MOCKS } from '@/config';
import { delay, request } from './client';
import { PRODUCTS } from './mock/products';
import { CATEGORIES } from './mock/categories';
import { DEFAULT_REVIEWS } from './mock/reviews';

/** Drops empty/false filters so they never reach the query string. */
function toQuery(filters = {}) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function listCategories({ signal } = {}) {
  if (USE_MOCKS) {
    await delay();
    return CATEGORIES;
  }
  return request('/api/categories', { signal });
}

/**
 * Books available for purchase. The backend gates on Published + public +
 * commercial, so anything returned here is sellable.
 *
 * `filters` accepts: cat, q, language, available_in, author, subject,
 * education_board, grade_level, org_id, tag, free_only, limit, skip.
 */
export async function listProducts({ signal, ...filters } = {}) {
  if (USE_MOCKS) {
    await delay();
    return PRODUCTS;
  }
  // The endpoint returns { items, total, limit, skip }; the catalog only needs
  // the page itself, so the envelope is unwrapped here rather than in the UI.
  const page = await request(`/api/products${toQuery(filters)}`, { signal });
  return page.items ?? [];
}

/** Distinct languages, authors, subjects, boards, grades, tags and publishers. */
export async function listFacets({ signal } = {}) {
  if (USE_MOCKS) {
    await delay();
    return {
      languages: [],
      additional_languages: [],
      authors: [],
      subjects: [],
      education_boards: [],
      grade_levels: [],
      tags: [],
      publishers: [],
    };
  }
  return request('/api/facets', { signal });
}

export async function getProduct(id, { signal } = {}) {
  if (USE_MOCKS) {
    await delay();
    const product = PRODUCTS.find((p) => String(p.id) === String(id));
    if (!product) throw new Error(`No product with id ${id}`);
    return product;
  }
  return request(`/api/products/${encodeURIComponent(id)}`, { signal });
}

/**
 * Sample images from inside a book — one auto-generated collage per chapter
 * that has one, in the book's own order.
 *
 * Its own call rather than a field on the product: `/products` deliberately
 * never carries chapter data, so this is only paid for on a detail page.
 * The mock catalog is the design prototype's, which has no chapters at all, so
 * offline the strip simply doesn't appear.
 */
export async function listPreviews(productId, { signal } = {}) {
  if (USE_MOCKS) {
    await delay();
    return [];
  }
  return request(`/api/products/${encodeURIComponent(productId)}/preview`, { signal });
}

export async function listReviews(productId, { signal } = {}) {
  if (USE_MOCKS) {
    await delay();
    return DEFAULT_REVIEWS;
  }
  return request(`/api/products/${encodeURIComponent(productId)}/reviews`, { signal });
}
